import * as React from 'react';
import {
  Alert,
  ActionButton,
  Card,
  Table,
  Tag,
  Modal,
  Switch,
  Input,
  Space,
  Popconfirm,
  Empty,
  message,
} from '@ui';
import { apiRequest, API_BASE } from '@lib/api';
import { t, getLanguage } from '@lib/i18n';
import { getCurrentUser } from '@lib/auth';
import { MaskedValueCell } from './MaskedValueCell';

const { useState, useEffect } = React;

interface InstallToken {
  id: number;
  user_id: number;
  token: string;
  note: string;
  enabled: boolean;
  tags: string[];
  used_count: number;
  created_at: string;
  updated_at: string;
}

interface InstallTokenListResp {
  tokens: InstallToken[];
  error?: string;
}

interface InstallTokenOpResp {
  error?: string;
}

const TOKEN_PATTERN = /^[^\s]{6,20}$/;

function InstallTokenManagement() {
  const [tokens, setTokens] = useState<InstallToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InstallToken | null>(null);
  const [token, setToken] = useState('');
  const [note, setNote] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { response, data } = await apiRequest<InstallTokenListResp>(`${API_BASE}/install-tokens`);
      if (response.ok) {
        setTokens(data.tokens || []);
      } else {
        message.error(data.error || t('installTokenManagement.loadFailed'));
      }
    } catch {
      message.error(t('installTokenManagement.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setToken('');
    setNote('');
    setTagsText('');
    setEnabled(true);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (tok: InstallToken) => {
    setEditing(tok);
    setToken(tok.token || '');
    setNote(tok.note || '');
    setTagsText((tok.tags || []).join(', '));
    setEnabled(tok.enabled);
    setError('');
    setModalOpen(true);
  };

  const save = async () => {
    setError('');
    if (!editing && !TOKEN_PATTERN.test(token)) {
      setError(t('installTokenManagement.tokenInvalid'));
      return;
    }
    const tagList = tagsText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      if (editing) {
        const { response, data } = await apiRequest<InstallTokenOpResp>(`${API_BASE}/install-tokens/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note, enabled, tags: tagList }),
        });
        if (response.ok) {
          message.success(t('installTokenManagement.updateSuccess'));
          setModalOpen(false);
          load();
        } else {
          setError(data.error || t('installTokenManagement.updateFailed'));
        }
      } else {
        const { response, data } = await apiRequest<InstallTokenOpResp>(`${API_BASE}/install-tokens`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, note, enabled, tags: tagList }),
        });
        if (response.ok) {
          message.success(t('installTokenManagement.createSuccess'));
          setModalOpen(false);
          load();
        } else {
          setError(data.error || t('installTokenManagement.createFailed'));
        }
      }
    } catch {
      setError(t('installTokenManagement.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tok: InstallToken) => {
    try {
      const { response, data } = await apiRequest<InstallTokenOpResp>(`${API_BASE}/install-tokens/${tok.id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        message.success(t('installTokenManagement.deleteSuccess'));
        load();
      } else {
        message.error(data.error || t('installTokenManagement.deleteFailed'));
      }
    } catch {
      message.error(t('installTokenManagement.networkError'));
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => message.success(t('common.copied')),
      () => message.error(t('common.copyFailed')),
    );
  };

  const formatTime = (v: string) =>
    v ? new Date(v).toLocaleString(getLanguage() === 'en-US' ? 'en-US' : 'zh-CN') : '-';

  const columns = [
    {
      title: t('installTokenManagement.token'),
      dataIndex: 'token',
      key: 'token',
      render: (text: string) => <MaskedValueCell value={text} head={6} tail={4} />,
    },
    { title: t('installTokenManagement.note'), dataIndex: 'note', key: 'note', render: (v: string) => v || '-' },
    {
      title: t('installTokenManagement.tags'),
      dataIndex: 'tags',
      key: 'tags',
      render: (list: string[]) => (list || []).map((tag) => <Tag key={tag}>{tag}</Tag>),
    },
    {
      title: t('installTokenManagement.enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (v: boolean) => <Tag color={v ? 'success' : 'default'}>{v ? t('common.enabled') : t('common.disabled')}</Tag>,
    },
    { title: t('installTokenManagement.usedCount'), dataIndex: 'used_count', key: 'used_count', width: 100 },
    {
      title: t('installTokenManagement.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (v: string) => formatTime(v),
    },
    {
      title: t('installTokenManagement.installCommand'),
      key: 'installCommand',
      render: (_: any, record: InstallToken) => {
        const account = getCurrentUser()?.username || '<ACCOUNT>';
        const dlURL = window.PidocBrand?.dlURL || 'https://dl.pidoc.cn';
        const cmd = `curl -fsSL ${dlURL}/pidoc/install.sh | bash -s -- --customtoken '${record.token}' --username '${account}'`;
        return (
          <ActionButton action="copy" onClick={() => copy(cmd)} title={cmd}>
            {t('installTokenManagement.copyInstall')}
          </ActionButton>
        );
      },
    },
    {
      title: t('common.operation'),
      key: 'action',
      width: 200,
      render: (_: any, record: InstallToken) => (
        <Space size="small">
          <ActionButton action="edit" onClick={() => openEdit(record)} />
          <Popconfirm title={t('installTokenManagement.confirmDelete')} onConfirm={() => remove(record)} okText={t('common.confirm')} cancelText={t('common.cancel')}>
            <ActionButton action="delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <div>
          <div className="text-base font-semibold text-white">{t('installTokenManagement.title')}</div>
          <p className="text-xs text-gray-400 mt-1 font-normal">{t('installTokenManagement.tip')}</p>
        </div>
      }
      extra={
        <ActionButton action="create" variant="primary" onClick={openCreate}>
          {t('installTokenManagement.create')}
        </ActionButton>
      }
    >
      <Table
        rowKey="id"
        dataSource={tokens}
        columns={columns}
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description={t('installTokenManagement.empty')} /> }}
      />
      <Modal
        title={editing ? t('installTokenManagement.editTitle') : t('installTokenManagement.createTitle')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnClose
        footer={
          <div className="flex justify-end gap-3 pt-4">
            <ActionButton action="cancel" onClick={() => setModalOpen(false)} disabled={saving} />
            <ActionButton action="save" variant="primary" onClick={save} disabled={saving} />
          </div>
        }
      >
        {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
        <div style={{ marginBottom: 12 }}>
          <label>{t('installTokenManagement.token')}</label>
          <Input
            value={token}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToken(e.target.value)}
            disabled={!!editing}
            placeholder={t('installTokenManagement.tokenPlaceholder')}
          />
          <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>{t('installTokenManagement.tokenHint')}</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>{t('installTokenManagement.note')}</label>
          <Input value={note} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)} placeholder={t('installTokenManagement.notePlaceholder')} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>{t('installTokenManagement.tags')}</label>
          <Input
            value={tagsText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagsText(e.target.value)}
            placeholder={t('installTokenManagement.tagsPlaceholder')}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Switch checked={enabled} onChange={(v: boolean) => setEnabled(v)} />
            <span>{t('installTokenManagement.enabled')}</span>
          </Space>
        </div>
      </Modal>
    </Card>
  );
}

export { InstallTokenManagement };
